import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getLighterThemeColor } from '../theme/colors';
import DownloadableFile from '../DownloadableFile';
import Icon from '../Icon';
import IconAsButton from '../IconAsButton';
import { media } from '../../utils/media';
import { useTranslation } from 'react-i18next';
import Typo from '../Typo';

const ControlsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressBar = styled.input`
  width: 100%;
  height: 4px;
  cursor: pointer;
  accent-color: ${(props) => props.$themeColor};
`;

const TimeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ControlsRow = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 15px;
  min-height: 36px; /* Prevents overlap with PlayButton */
  margin-top: 6px;

  ${media.lessThan('tablet')} {
    gap: 10px;
    min-height: 32px;
  }

  ${media.lessThan('phablet')} {
    gap: 8px;
    min-height: 28px;
  }
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
`;

const VolumeSlider = styled.input`
  width: 80px;
  height: 5px;
  cursor: pointer;
  accent-color: ${({ $themeColor }) => $themeColor};

  ${media.lessThan('tablet')} {
    width: 60px;
  }

  ${media.lessThan('phablet')} {
    width: 50px;
  }
`;

const PlayButton = styled.button`
  position: absolute;
  left: 50%;
  top: -18px;
  transform: translateX(-50%);
  background: ${(props) => props.$themeColor};
  border: none;
  border-radius: 50%;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${(props) => props.$themeColor};
    transform: translateX(-50%) scale(1.1);
  }
  & i {
    padding-top: 4px;
  }
`;

const DownloadContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Controls = ({ themeColor, wavesurfer, audioRef, downloadFile }) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const isWaveSurfer = !!wavesurfer;

  useEffect(() => {
    const audioElement = audioRef?.current;
    if (isWaveSurfer && wavesurfer) {
      const updateTime = () => setCurrentTime(wavesurfer.getCurrentTime());
      setDuration(wavesurfer.getDuration());
      wavesurfer.setVolume(volume);
      wavesurfer.on('audioprocess', updateTime);
      wavesurfer.on('seek', updateTime);
      wavesurfer.on('play', () => setPlaying(true));
      wavesurfer.on('pause', () => setPlaying(false));
      return () => {
        wavesurfer.un('audioprocess', updateTime);
        wavesurfer.un('seek', updateTime);
        wavesurfer.un('play');
        wavesurfer.un('pause');
      };
    } else if (audioElement) {
      const handleLoadedMetadata = () => {
        setDuration(audioElement.duration || 0);
      };
      const updateTime = () => setCurrentTime(audioElement.currentTime);
      const handlePlay = () => setPlaying(true);
      const handlePause = () => setPlaying(false);

      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.addEventListener('timeupdate', updateTime);
      audioElement.addEventListener('play', handlePlay);
      audioElement.addEventListener('pause', handlePause);

      audioElement.volume = volume;

      return () => {
        audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.removeEventListener('timeupdate', updateTime);
        audioElement.removeEventListener('play', handlePlay);
        audioElement.removeEventListener('pause', handlePause);
      };
    }
  }, [audioRef?.current, wavesurfer, volume, isWaveSurfer]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    const audioElement = audioRef?.current;
    if (isWaveSurfer) {
      if (playing) wavesurfer.pause();
      else wavesurfer.play();
    } else if (audioElement) {
      if (playing) audioElement.pause();
      else audioElement.play();
    }
  };

  const handleProgressChange = (e) => {
    const newTime = (e.target.value / 100) * duration;
    if (isWaveSurfer && duration > 0) {
      wavesurfer.seekTo(newTime / duration);
    } else if (audioRef?.current && duration > 0) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value) / 100;
    setVolume(newVolume);
    setPreviousVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (isWaveSurfer) {
      wavesurfer.setVolume(newVolume);
    } else if (audioRef?.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const toggleMute = () => {
    if (isMuted) {
      setVolume(previousVolume);
      if (isWaveSurfer) wavesurfer.setVolume(previousVolume);
      else if (audioRef?.current) audioRef.current.volume = previousVolume;
    } else {
      setPreviousVolume(volume);
      setVolume(0);
      if (isWaveSurfer) wavesurfer.setVolume(0);
      else if (audioRef?.current) audioRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const lighterColor = getLighterThemeColor(themeColor);

  return (
    <ControlsContainer>
      <ProgressBar
        type='range'
        min='0'
        max='100'
        value={duration > 0 ? (currentTime / duration) * 100 : 0}
        onChange={handleProgressChange}
        $themeColor={themeColor}
      />
      <TimeRow>
        <Typo.Info>{formatTime(currentTime)}</Typo.Info>
        <Typo.Info>{formatTime(duration)}</Typo.Info>
      </TimeRow>
      <ControlsRow>
        <VolumeContainer>
          <IconAsButton
            iconName={isMuted ? 'VolumeOff' : 'VolumeOn'}
            color={themeColor}
            clickHandle={toggleMute}
            size='small'
            tooltipLabel={isMuted ? t('globals.unmute') : t('globals.mute')}
          />
          <VolumeSlider type='range' min='0' max='100' value={volume * 100} onChange={handleVolumeChange} $themeColor={themeColor} />
        </VolumeContainer>
        <PlayButton $themeColor={themeColor} onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? <Icon.Pause color={lighterColor} /> : <Icon.Play color={lighterColor} />}
        </PlayButton>
        <DownloadContainer>
          {downloadFile && <DownloadableFile.AsIcon iconName='Download' file={downloadFile} color={themeColor} size='small' />}
        </DownloadContainer>
      </ControlsRow>
    </ControlsContainer>
  );
};

export default Controls;
